import React from 'react'
import clsx from 'clsx'
import { KeyValueText } from 'components'
import { Typography, useStringOrFallback } from 'cs-kevlar-react'
import { createStyles, makeStyles, Theme } from 'cs-kevlar-react'
import { ClassNameMap } from '@mui/styles'
import { SL90, EL90 } from 'cs-kevlar-react/lib/es/theming/palette'

type ClassNames = 'keyValueText' | 'diffAdded' | 'diffRemoved' | 'value'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    keyValueText: {
      paddingTop: theme.spacing(8),
    },
    value: {
      wordBreak: 'break-all',
    },
    diffAdded: {
      background: SL90,
      display: 'inline-block',
      padding: theme.spacing(0, 1),
    },
    diffRemoved: {
      background: EL90,
      textDecoration: 'line-through',
      display: 'inline-block',
      marginRight: theme.spacing(2),
      padding: theme.spacing(0, 1),
    },
  }),
)

const isEmpty = (value: string | null | undefined) =>
  !value || value.length === 0 || !value.trim()

const renderDiffContent = (
  original: string | null | undefined,
  updated: string | null | undefined,
  classes: ClassNameMap<ClassNames>,
) => {
  if (original === updated || (isEmpty(original) && isEmpty(updated))) {
    return (
      <Typography className={classes.value}>
        {useStringOrFallback(original)}
      </Typography>
    )
  }

  return [
    isEmpty(original) ? null : (
      <Typography
        key="original"
        className={clsx(classes.value, classes.diffRemoved)}
      >
        {original}
      </Typography>
    ),
    isEmpty(updated) ? null : (
      <Typography
        key="updated"
        className={clsx(classes.value, classes.diffAdded)}
      >
        {updated}
      </Typography>
    ),
  ]
}

interface DetailFieldProps {
  label: string
  original: string | null | undefined
  updated?: string | null | undefined
  showDiff?: boolean
}

export const DetailField: React.FunctionComponent<DetailFieldProps> = ({
  label,
  original,
  updated,
  showDiff = false,
}) => {
  const classes = useStyles({})

  return (
    <React.Fragment>
      {!showDiff && (
        <KeyValueText
          label={label}
          className={classes.keyValueText}
          valueClassName={classes.value}
        >
          {updated ?? original}
        </KeyValueText>
      )}

      {showDiff && (
        // By giving the KeyValueText a function instead of a direct child,
        // it won't wrap the content in a Typography component, but use the content directly.
        <KeyValueText label={label} className={classes.keyValueText}>
          {() => renderDiffContent(original, updated, classes)}
        </KeyValueText>
      )}
    </React.Fragment>
  )
}
